import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './leaderboard.css';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { apiUrl } from '../../utils/apiUrl';
import { baseUserId, getAuthBaseUserId, isMultiplaySession } from '../../utils/multiplay';

const TOP_N = 5;

function parseTimeToSeconds(t) {
  if (t == null) return 0;
  if (typeof t === 'number' && Number.isFinite(t)) return Math.max(0, Math.floor(t));
  if (typeof t !== 'string') return 0;
  const s = String(t).trim();
  if (/^\d+$/.test(s)) return parseInt(s, 10) || 0;
  const parts = t.split(':').map((p) => parseInt(String(p).trim(), 10) || 0);
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return 0;
}

/** Display time as `MM:SS` (e.g. 185 → 03:05). */
function formatPodiumTime(raw) {
  if (raw == null || raw === '') return '00:00';

  // Seconds as number
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const sec = Math.max(0, Math.floor(raw));
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }

  const s = String(raw).trim();

  // Seconds as digits string
  if (/^\d+$/.test(s)) {
    const sec = Math.max(0, parseInt(s, 10) || 0);
    const mm = Math.floor(sec / 60);
    const ss = sec % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }

  // Parse `m:s`, `mm:ss`, or `hh:mm:ss` (fallbacks handled by parseTimeToSeconds)
  const sec = parseTimeToSeconds(s);
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function pickDisplayScore(m) {
  const keys = ['points', 'total_score', 'spot_score', 'score'];
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (!(key in m)) continue;
    const v = m[key];
    if (v == null || v === '') continue;
    const n = parseInt(String(v).trim(), 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

function pickDisplayTime(m) {
  const raw = m.time ?? m.reportTime;
  if (raw == null || raw === '') return '00:00';
  if (typeof raw === 'number' && Number.isFinite(raw)) return String(Math.max(0, Math.floor(raw)));
  const s = String(raw).trim().slice(0, 50);
  return s || '00:00';
}

function parsePositiveRank(v) {
  const n = parseInt(String(v ?? '').trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function normalizeLeaderboardMatch(m) {
  return { ...m, score: pickDisplayScore(m), time: pickDisplayTime(m) };
}

function cleanDisplayName(raw) {
  const s = String(raw ?? "").trim();
  if (!s || /^null(\s+null)?$/i.test(s) || /^(player|you)$/i.test(s)) return "";
  return s;
}

function nameFromEmail(email) {
  const e = String(email ?? "").trim();
  if (!e || !e.includes("@")) return "";
  const local = e.split("@")[0].trim();
  return local || "";
}

/** Prefer auth user name for self; otherwise row name, then email local-part. */
function displayLeaderboardName(row, authUser, isCurrentUser = false) {
  if (isCurrentUser) {
    const me = cleanDisplayName(authUser?.name);
    if (me) return me;
  }

  const fromRow = cleanDisplayName(row?.name);
  if (fromRow) return fromRow;

  const email =
    row?.email ||
    (isCurrentUser ? authUser?.email : "") ||
    "";
  const fromEmail = nameFromEmail(email);
  if (fromEmail) return fromEmail;

  if (isCurrentUser) {
    const me = cleanDisplayName(authUser?.name);
    if (me) return me;
  }

  return "—";
}

/** Alias for legacy / HMR bundles that still reference displayNameOf */
function displayNameOf(row, authUser, isCurrentUser = false) {
  return displayLeaderboardName(row, authUser, isCurrentUser);
}

/** One row per userId — keep highest score (map session can duplicate rows). */
function dedupeByUserIdBestScore(rows) {
  const map = new Map();
  for (const r of rows) {
    const id =
      r.userId != null && r.userId !== ''
        ? baseUserId(r.userId)
        : `k:${r.email || ''}:${r.name || ''}`;
    const prev = map.get(id);
    const score = r.score ?? 0;
    if (!prev || score > (prev.score ?? 0)) {
      map.set(id, r);
    } else if (prev && score === (prev.score ?? 0)) {
      const ta = parseTimeToSeconds(String(r.time));
      const tb = parseTimeToSeconds(String(prev.time));
      if (ta < tb) map.set(id, r);
    }
  }
  return [...map.values()];
}

function Leaderboard() {
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData } = useSelector((state) => state.theme);
  const [matches, setMatches] = useState([]);
  const [currentUserMeta, setCurrentUserMeta] = useState(null); // { rank, score, time, name }
  const [fetchError, setFetchError] = useState('');
  const [loading, setLoading] = useState(true);
  const [feedbackDone, setFeedbackDone] = useState(
    () => !!location.state?.feedbackSubmitted
  );

  const titleTextColor =
    themeData?.landing_page_title_color || themeData?.text_color || "#4f3216";

  useEffect(() => {
    if (!user || !adminToken) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      setFetchError('');
      try {
        const uid = getAuthBaseUserId(user);
        const resp = await fetch(apiUrl('/gameMatchUsers'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !data.success) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(
            data.error || data.message || (resp.status ? `Request failed (${resp.status})` : 'Leaderboard request failed')
          );
          return;
        }
        let raw = Array.isArray(data.matches) ? data.matches : [];
        if (raw.length === 0 && data.currentUser) {
          const cu = data.currentUser;
          const id = cu.userId ?? cu.id;
          if (id != null && String(id).trim() !== '') {
            raw = [{ ...cu, userId: id, isSelf: true, matchPercent: 0 }];
          }
        }
        const normalized = raw.map(normalizeLeaderboardMatch);
        setMatches(dedupeByUserIdBestScore(normalized));
        const cu = data.currentUser;
        const apiRank = data.currentUserRank;
        if (cu && (cu.userId != null || cu.id != null)) {
          const cuNorm = normalizeLeaderboardMatch({ ...cu, userId: cu.userId ?? cu.id, isSelf: true });
          setCurrentUserMeta({
            rank: parsePositiveRank(apiRank),
            score: cuNorm.score ?? 0,
            time: cuNorm.time,
            name: displayLeaderboardName(cuNorm, user, true),
          });
        } else {
          setCurrentUserMeta(
            parsePositiveRank(apiRank)
              ? {
                  rank: parsePositiveRank(apiRank),
                  score: 0,
                  time: '00:00',
                  name: displayLeaderboardName(null, user, true),
                }
              : null
          );
        }
      } catch (e) {
        console.error('gameMatchUsers', e);
        if (!cancelled) {
          setMatches([]);
          setCurrentUserMeta(null);
          setFetchError(e.message || 'Network error loading leaderboard');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      
    })();

    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  useEffect(() => {
    if (!user || !adminToken) return;
    if (isMultiplaySession(user)) return;
    let cancelled = false;
    (async () => {
      try {
        const uid = getAuthBaseUserId(user);
        const resp = await fetch(apiUrl('/spotGameStatus'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            isMultiplaySession: user.isMultiplaySession,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = data.userFeedback;
        if (data.success && raw != null && String(raw).trim() !== '') {
          setFeedbackDone(true);
        }
      } catch (e) {
        console.error('spotGameStatus (leaderboard)', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken]);

  const btnBg = themeData?.button_backgroundcolor || '#644b23';
  const btnText = themeData?.button_color || '#fffef8';
  const gridCellBg = themeData?.word_grid_bg_color || "#fff8ef";
  const gridCellText = themeData?.word_grid_text_color || "#3b2715";

  const sortedRows = useMemo(() => {
    const copy = [...matches];
    copy.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(String(a.time)) - parseTimeToSeconds(String(b.time));
    });
    return copy.map((m, i) => ({ ...m, displayRank: i + 1 }));
  }, [matches]);

  const topFive = useMemo(() => sortedRows.slice(0, TOP_N), [sortedRows]);
  const currentUserRankRow = useMemo(() => {
    if (!sortedRows.length) return null;
    const uid = getAuthBaseUserId(user);
    if (!uid) return sortedRows.find((r) => r.isSelf) || null;
    return (
      sortedRows.find((r) => baseUserId(r.userId) === uid) ||
      sortedRows.find((r) => r.isSelf) ||
      null
    );
  }, [sortedRows, user]);
  const currentUserSummary = useMemo(() => {
    if (currentUserRankRow) {
      return {
        rank: currentUserRankRow.displayRank,
        score: currentUserRankRow.score ?? 0,
        time: currentUserRankRow.time,
        name: displayLeaderboardName(currentUserRankRow, user, true),
      };
    }
    if (currentUserMeta) {
      return {
        rank: currentUserMeta.rank ?? null,
        score: currentUserMeta.score ?? 0,
        time: currentUserMeta.time,
        name: displayLeaderboardName({ name: currentUserMeta.name, email: user?.email }, user, true),
      };
    }
    return null;
  }, [currentUserRankRow, currentUserMeta]);
  const resolvedCurrentUserRank = useMemo(() => {
    const apiRank = parsePositiveRank(currentUserSummary?.rank);
    if (apiRank) return apiRank;
    if (!currentUserSummary) return null;
    const myId = getAuthBaseUserId(user);
    const meKey = myId || `self:${String(currentUserSummary.name || 'you').toLowerCase()}`;
    const poolMap = new Map();
    sortedRows.forEach((row) => {
      const key =
        row.userId != null && row.userId !== ''
          ? baseUserId(row.userId)
          : `n:${String(row.name || '').toLowerCase()}`;
      if (!poolMap.has(key)) poolMap.set(key, row);
    });
    if (!poolMap.has(meKey)) {
      poolMap.set(meKey, {
        userId: myId || null,
        name: displayLeaderboardName({ name: currentUserSummary.name, email: user?.email }, user, true),
        score: currentUserSummary.score ?? 0,
        time: currentUserSummary.time ?? '00:00',
      });
    }
    const pool = Array.from(poolMap.values());
    pool.sort((a, b) => {
      const sb = b.score ?? 0;
      const sa = a.score ?? 0;
      if (sb !== sa) return sb - sa;
      return parseTimeToSeconds(String(a.time)) - parseTimeToSeconds(String(b.time));
    });
    const idx = pool.findIndex((row) => {
      if (myId && row.userId != null && baseUserId(row.userId) === myId) return true;
      if (!myId) {
        return String(row.name || '').trim().toLowerCase() === String(currentUserSummary.name || '').trim().toLowerCase();
      }
      return false;
    });
    return idx >= 0 ? idx + 1 : null;
  }, [currentUserSummary, sortedRows, user]);

  const topFiveContainsCurrentUser = useMemo(() => {
    const myId = getAuthBaseUserId(user);
    return topFive.some((row) => {
      if (myId && row.userId != null && baseUserId(row.userId) === myId) return true;
      return !!row.isSelf;
    });
  }, [topFive, user]);

  const leaderboardRows = useMemo(() => {
    const rows = topFive.map((row) => ({
      ...row,
      isCurrentUser:
        (!!row.isSelf && row.isSelf === true) ||
        (getAuthBaseUserId(user) !== "" &&
          baseUserId(row.userId) === getAuthBaseUserId(user)),
    }));

    if (!topFiveContainsCurrentUser && currentUserSummary) {
      rows.push({
        userId: getAuthBaseUserId(user) || "self",
        displayRank: resolvedCurrentUserRank ?? TOP_N + 1,
        name: currentUserSummary.name,
        score: currentUserSummary.score ?? 0,
        time: currentUserSummary.time,
        isCurrentUser: true,
      });
    }
    return rows;
  }, [topFive, topFiveContainsCurrentUser, currentUserSummary, resolvedCurrentUserRank, user]);

  return (
    <div className="lb-page">
      <div className="lb-inner">
        <header className="lb-hero">
          <h1
            className="lb-heading-main"
            style={{ "--lb-heading-color": titleTextColor, color: titleTextColor }}
          >
            Leaderboard
          </h1>
        </header>

        <div className="lb-table-shell">
          {loading ? (
            <div className="lb-loading">
              <div className="lb-spinner" />
              <span>Loading rankings…</span>
            </div>
          ) : fetchError ? (
            <div className="lb-empty lb-empty--error">
              <p>{fetchError}</p>
              <p className="lb-empty-hint">Check that the API is running and your session matches the game data.</p>
            </div>
          ) : matches.length === 0 ? (
            <div className="lb-empty">
              <p>No data available yet.</p>
            </div>
          ) : (
            <div className="lb-table-wrap">
              <table
                className="lb-table"
                aria-label="Top 5 leaderboard"
                style={{
                  "--lb-head-bg": btnBg,
                  "--lb-head-fg": btnText,
                  "--lb-cell-bg": gridCellBg,
                  "--lb-cell-fg": gridCellText,
                }}
              >
                <thead>
                  <tr>
                    <th>Rank</th>
                    <th>Name</th>
                    <th>Points</th>
                    <th>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {leaderboardRows.map((m) => (
                    <tr
                      key={m.userId || `${m.name}-${m.displayRank}`}
                      className={m.isCurrentUser ? "lb-current-row" : ""}
                    >
                      <td>#{m.displayRank}</td>
                      <td>{displayLeaderboardName(m, user, m.isCurrentUser)}</td>
                      <td>{m.score ?? 0}</td>
                      <td>{formatPodiumTime(m.time)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="lb-feedback-wrap">
          {feedbackDone ? (
            <div
              className="lb-feedback-done"
              style={{ background: btnBg, color: btnText }}
            >
              Thanks — you’ve submitted feedback for this session.
            </div>
          ) : (
            <Link
              to="/feedback" style={{ color: btnText }}
              replace
              className="lb-feedback-link"
            
            >
              Rate your experience
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default Leaderboard;
